import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, signInWithRedirect, signIn } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import OAuthButton from './OAuthButton';
import CountryAvailabilityModal from './CountryAvailabilityModal';
import { v4 as uuidv4 } from 'uuid';
import configService from '../services/configService';
import Logo from './Logo';


function Signup() {
  const navigate = useNavigate();
  const { user, logout, checkAuthState } = useAuth();
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    phone: '',
    businessType: '',
    password: '',
    confirmPassword: ''
  });
  const [countryCode, setCountryCode] = useState('+1');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isBetaTester, setIsBetaTester] = useState(false);

  useEffect(() => {
    // Capture referral code and beta tester flag from URL on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    const betaTester = urlParams.get('betatester');
    
    if (refCode) {
      console.log('🎁 Referral code captured from URL:', refCode);
      setReferralCode(refCode);
      localStorage.setItem('referralCode', refCode);
    }
    
    if (betaTester === 'smb') {
      console.log('🧪 Beta tester signup detected');
      setIsBetaTester(true);
    }
  }, []);

  useEffect(() => {
    console.log('🔍 Signup useEffect - user state changed:', {
      hasUser: !!user,
      userEmail: user?.attributes?.email,
      showConfirmation,
      currentUrl: window.location.href
    });
    
    if (user && !showConfirmation) {
      console.log('🔍 User exists (OAuth flow), tracking referral and navigating to /plans');
      
      // Track referral signup for OAuth users
      const cognitoUserId = user.attributes?.sub;
      if (cognitoUserId) {
        trackReferralSignup(cognitoUserId);
      }
      
      // Clear any pending navigation timeout
      if (window.signupNavigationTimeout) {
        clearTimeout(window.signupNavigationTimeout);
        window.signupNavigationTimeout = null;
      }
      
      navigate('/plans');
    }
  }, [user, navigate, showConfirmation]);

  const handleLogout = async () => {
    try {
      await logout();
      setError('');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const signupWith = async (provider) => {
    try {
      console.log('Starting OAuth flow with provider:', provider);
      setLoading(true);
      await signInWithRedirect({ provider });
      console.log('signInWithRedirect called successfully');
    } catch (error) {
      console.error('OAuth error:', error);
      setError(`${provider} signup failed: ${error.message}`);
      setLoading(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleSignup started:', {
      email: formData.email,
      hasUser: !!user,
      userEmail: user?.attributes?.email,
      showConfirmation
    });
    
    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Allow signup to proceed for all countries - we'll handle availability in the dashboard
    // International users will be created in Cognito but shown availability modal on login
    
    // Validate phone number format (E.164)
    const fullPhone = countryCode + formData.phone;
    const phoneRegex = /^\+[1-9]\d{10,14}$/;
    if (!phoneRegex.test(fullPhone)) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }
    
    try {
      // Generate business_id for new users
      const businessId = uuidv4();
      
      console.log('🔍 Calling signUp with:', {
        username: formData.email,
        businessId
      });
      
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            phone_number: countryCode + formData.phone,
            'custom:business_name': formData.businessName,
            'custom:business_type': formData.businessType,
            'custom:business_id': businessId,
            'custom:country_code': countryCode,
            'custom:beta_tester': isBetaTester ? 'true' : 'false'
          }
        }
      });
      
      console.log('🔍 signUp successful, showing confirmation');
      setShowConfirmation(true);
    } catch (error) {
      console.log('🔍 signUp error:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      
      // If user already exists but isn't confirmed, show confirmation screen
      if (error.message.includes('User already exists') || error.name === 'UsernameExistsException') {
        console.log('🔍 User exists but unconfirmed, showing confirmation');
        setShowConfirmation(true);
        setError('Account exists but needs confirmation. Please check your email.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmation = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleConfirmation started:', {
      email: formData.email,
      confirmationCode,
      hasUser: !!user,
      userEmail: user?.attributes?.email
    });
    
    try {
      console.log('🔍 Calling confirmSignUp');
      await confirmSignUp({
        username: formData.email,
        confirmationCode
      });
      
      console.log('🔍 confirmSignUp successful, signing in');
      
      // Automatically sign in after confirmation
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      console.log('🔍 signIn successful:', {
        userId: result?.userId,
        isSignedIn: result?.isSignedIn
      });
      
      // Log user login after successful confirmation and sign in
      await logger.logUserLogin(result);
      
      // Wait for auth state to update and get the cognito user ID
      await new Promise(resolve => setTimeout(resolve, 1000));
      await checkAuthState();
      
      // Track referral signup if referral code exists
      // Get cognito user ID from the JWT token or result
      let cognitoUserId = result?.userId;
      if (!cognitoUserId) {
        // Fallback: extract from localStorage JWT
        try {
          const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
          if (lastAuthUser) {
            const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
            const idToken = localStorage.getItem(idTokenKey);
            if (idToken) {
              const payload = JSON.parse(atob(idToken.split('.')[1]));
              cognitoUserId = payload.sub;
            }
          }
        } catch (error) {
          console.error('Failed to extract cognito user ID:', error);
        }
      }
      
      console.log('🎁 Tracking referral with cognito user ID:', cognitoUserId);
      await trackReferralSignup(cognitoUserId);
      
      console.log('🔍 Manually refreshing auth state before navigation');
      // Wait a moment for tokens to be stored, then refresh auth state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Manually trigger auth state check
      await checkAuthState();
      
      // The useAuth context should automatically detect the change and navigate
      // But if it doesn't work within 2 seconds, navigate manually
      const navigationTimeout = setTimeout(() => {
        console.log('🔍 Navigation timeout - forcing navigation to /plans');
        navigate('/plans');
      }, 2000);
      
      // Clear timeout if user state updates (handled by useEffect)
      window.signupNavigationTimeout = navigationTimeout;
      
    } catch (error) {
      console.log('🔍 handleConfirmation error:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const trackReferralSignup = async (cognitoUserId) => {
    const refCode = localStorage.getItem('referralCode');
    if (refCode && cognitoUserId) {
      try {
        console.log('🎁 Tracking referral signup:', { refCode, cognitoUserId });
        await configService.trackReferralSignup(refCode, cognitoUserId);
        console.log('✅ Referral signup tracked successfully');
        localStorage.removeItem('referralCode');
      } catch (error) {
        console.error('❌ Failed to track referral signup:', error);
        // Don't throw error - referral tracking failure shouldn't block signup
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div>
          <Link to="/" className="no-underline">
            <Logo height="32px" />
          </Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-gray-700 hover:text-blue-500 no-underline">Home</Link>
          <Link to="/login" className="text-gray-700 hover:text-blue-500 no-underline">Login</Link>
          {user && (
            <>
              <span className="text-sm text-gray-600">
                There is already a signed in user.
              </span>
              <button 
                onClick={handleLogout}
                className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
              >
                Logout
              </button>
            </>
          )}
        </div>
      </nav>

      <div className="flex justify-center items-center min-h-screen pt-20 px-8 bg-gray-50">
        <div className="bg-white p-12 rounded-2xl shadow-xl w-full max-w-md text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start Your Free Trial</h1>
          <p className="text-gray-600 mb-4">Get your AI assistant working in 5 minutes</p>
          {referralCode && (
            <div className="bg-green-100 text-green-700 p-3 rounded-xl mb-4 text-sm">
              🎁 You're signing up with a referral! Your friend will get 1 month free when you complete your trial.
            </div>
          )}

          {error && (
            <div className="bg-red-100 text-red-600 p-4 rounded-xl mb-4 text-center">
              {error}
            </div>
          )}

          {!showConfirmation ? (
            <>
              <div className="mb-8 space-y-3">
                <OAuthButton 
                  provider="google"
                  onClick={() => signupWith('Google')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="facebook"
                  onClick={() => signupWith('Facebook')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="apple"
                  onClick={() => signupWith('SignInWithApple')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
                <OAuthButton 
                  provider="microsoft"
                  onClick={() => signupWith('LoginWithAmazon')}
                  className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                />
              </div>

              <div className="my-8 text-gray-600 relative">
                <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200"></div>
                <span className="bg-white px-4">or</span>
              </div>

              <form onSubmit={handleSignup} className="flex flex-col gap-4">
                <input 
                  type="text" 
                  name="businessName"
                  placeholder="Business Name" 
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required 
                  className="p-4 border-2 border-gray-200 rounded-xl text-base"
                />
                <input 
                  type="email" 
                  name="email"
                  placeholder="Email Address" 
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                  className="p-4 border-2 border-gray-200 rounded-xl text-base"
                />
                <div className="flex gap-2">
                  <select 
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="p-4 border-2 border-gray-200 rounded-xl text-base w-24"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+81">🇯🇵 +81</option>
                  </select>
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="Phone Number" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    required 
                    className="p-4 border-2 border-gray-200 rounded-xl text-base flex-1"
                  />
                </div>
                <select 
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  required 
                  className="p-4 border-2 border-gray-200 rounded-xl text-base"
                >
                  <option value="">What type of business?</option>
                  <option value="salon">Hair Salon / Barbershop</option>
                  <option value="spa">Spa / Wellness</option>
                  <option value="dental">Dental Practice</option>
                  <option value="medical">Medical Practice</option>
                  <option value="auto">Auto Service</option>
                  <option value="home">Home Services</option>
                  <option value="other">Other</option>
                </select>
                <input 
                  type="password" 
                  name="password"
                  placeholder="Create Password (min 8 characters)" 
                  value={formData.password}
                  onChange={handleInputChange}
                  required 
                  minLength="8"
                  className="p-4 border-2 border-gray-200 rounded-xl text-base"
                />
                <input 
                  type="password" 
                  name="confirmPassword"
                  placeholder="Confirm Password" 
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required 
                  minLength="8"
                  className="p-4 border-2 border-gray-200 rounded-xl text-base"
                />
                <button 
                  type="submit" 
                  disabled={loading}
                  className={`btn-primary text-lg ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {loading ? 'Creating Account...' : 'Start Free Trial'}
                </button>
              </form>
            </>
          ) : (
            <form onSubmit={handleConfirmation} className="flex flex-col gap-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Check Your Email</h2>
              <p className="text-gray-600 mb-4">
                We sent a confirmation code to {formData.email}
              </p>
              <input 
                type="text" 
                placeholder="Enter confirmation code" 
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required 
                className="p-4 border-2 border-gray-200 rounded-xl text-base text-center"
              />
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-lg ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
            </form>
          )}

          {!showConfirmation && (
            <>
              <p className="mt-4 text-sm text-gray-600">
                No credit card required • 14-day free trial
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-500 hover:text-blue-600 font-semibold no-underline">
                  Sign in here
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
      
      <CountryAvailabilityModal 
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        countryCode={countryCode}
        email={formData.email}
      />
    </>
  );
}

export default Signup;